import React, { useEffect, useRef, useCallback } from "react";
import L from "leaflet";
import "leaflet.markercluster";
import { useMap } from "react-leaflet";
import { useNavigate } from "react-router-dom";
import { createMarkerSVG, createLockSVG } from "../constants/mapConstants";

const ProjectMarkers = ({ markers, visible, cluster }) => {
  const map = useMap();
  const navigate = useNavigate();
  const markerLayerRef = useRef(null);
  const markersCreated = useRef({});

  useEffect(() => {
    if (!window.markerRegistry) {
      window.markerRegistry = {};
    }
  }, []);

  const navigateTo = useCallback(
    (url) => {
      if (url.startsWith("http")) {
        window.location = url;
      } else {
        navigate(url);
      }
    },
    [navigate]
  );

  const createMarkers = useCallback(() => {
    if (!visible || !markers || markers.length === 0 || !map) {
      return null;
    }
    const markerGroup = cluster
      ? L.markerClusterGroup({
          chunkedLoading: true,
          removeOutsideVisibleBounds: false,
          spiderfyOnMaxZoom: false,
          disableClusteringAtZoom: 7,
          maxClusterRadius: (zoom) => {
            if (zoom <= 4) return 160; 
            if (zoom <= 5) return 100; 
            if (zoom <= 6) return 30; 
            if (zoom <= 6.5) return 20; 
            return 160;
          },
          animate: false,
          zoomToBoundsOnClick: true,
          showCoverageOnHover: true,
        })
      : L.featureGroup();

    markers.forEach(({ project }) => {
      if (!project?.latitude || !project?.longitude || isNaN(Number(project.latitude)) || isNaN(Number(project.longitude))) {
        return;
      }

      if (project.project_id) {
        window.markerRegistry[project.project_id] = {
          name: project.project_name,
          lat: project.latitude,
          lng: project.longitude,
          rendered: true,
        };
      }

      const color = project.legacy_url ? "#333333" : "#FF0000";
      const svg = project.public ? createMarkerSVG(color) : createLockSVG(color);
      const iconUrl = "data:image/svg+xml;charset=UTF-8," + encodeURIComponent(svg);

      const icon = L.icon({
        iconUrl,
        iconSize: [16, 16],
        iconAnchor: [8, 8],
      });

      const marker = L.marker([Number(project.latitude), Number(project.longitude)], {
        icon,
        pane: "markerPane",
      });

      const urlCompany = project.company_slug !== null ? `/c/${project.company_slug}` : `/g/${project.company_id}`;
      const urlProject = project.legacy_url ? project.legacy_url : `${urlCompany}/${project.project_slug}`;

      const tooltipDiv = document.createElement("div");
      tooltipDiv.className = "InventumMapTooltipBackboard";

      const projectNameSpan = document.createElement("span");
      projectNameSpan.className = "map-project-name";
      projectNameSpan.textContent = project.project_name || "Unnamed Project";
      projectNameSpan.style.cursor = "pointer";
      projectNameSpan.setAttribute("data-url", urlProject);
      tooltipDiv.appendChild(projectNameSpan);

      const companyNameSpan = document.createElement("span");
      companyNameSpan.className = "map-company-name";
      companyNameSpan.textContent = project.company_name || "Unnamed Company";
      companyNameSpan.style.cursor = "pointer";
      companyNameSpan.setAttribute("data-url", urlCompany);
      tooltipDiv.appendChild(companyNameSpan);

      marker.bindTooltip(tooltipDiv, {
        permanent: true,
        direction: project.flip_map_label ? "left" : "right",
        className: project.flip_map_label ? "InventumMapTooltip reverseTooltip" : "InventumMapTooltip",
        offset: [0, 0],
      });

      projectNameSpan.addEventListener("click", (e) => {
        e.stopPropagation();
        if (project.legacy_url) {
          window.location = project.legacy_url;
        } else {
          navigateTo(urlProject);
        }
      });

      companyNameSpan.addEventListener("click", (e) => {
        e.stopPropagation();
        navigateTo(urlCompany);
      });

      marker.on("click", (e) => {
        L.DomEvent.stopPropagation(e);

        const target = e.originalEvent?.target;

        if (target && target.classList.contains("map-company-name")) {
          navigateTo(urlCompany);
        } else if (target && target.classList.contains("map-project-name")) {
          if (project.legacy_url) {
            window.location = project.legacy_url;
          } else {
            navigateTo(urlProject);
          }
        } else {
          if (project.legacy_url) {
            window.location = project.legacy_url;
          } else {
            navigateTo(urlProject);
          }
        }
      });

      markerGroup.addLayer(marker);
    });

    return markerGroup;
  }, [map, markers, visible, cluster, navigateTo]);

  useEffect(() => {
    const markerGroup = createMarkers();

    if (markerGroup) {
      map.addLayer(markerGroup);
      markerLayerRef.current = markerGroup;
    }

    return () => {
      if (markerLayerRef.current) {
        map.removeLayer(markerLayerRef.current);
        markerLayerRef.current = null;
        markersCreated.current = {};
      }
    };
  }, [map, markers, visible, cluster, createMarkers]);

  return null;
};

export default ProjectMarkers;
